import React, { useMemo } from "react";
import ExchangePill from "../../../../../components/ExchangePill";
import "./ordersTabs.scss";

import type {
  BalanceDisplayProps,
  OrderPayload,
} from "../../../../../models/orders";
import type { OrdersTabsProps } from ".";

import {
  getOrderProduct,
  isErrorResponse,
  isSuccessResponse,
  orderMultiplier,
} from "../../orders.helper";

// @hooks
import useOrderRequest from "../../../../../hooks/api/get/getMarginsOrders";

import RectangleSkelton from "../../../../../components/Alerts/common/RectangleSkeltion";

import { useDispatch } from "react-redux";
import { setMargin } from "../../../../../slices/ordersSlice";

type TabProps = {
  timeframe: string;
  isSelected: boolean;
  isDisabled: boolean;
  onClick: () => void;
  exchange: string;
  quantity: number;
  segMentId: number;
  price: number;
} & Pick<
  OrdersTabsProps,
  "ltp" | "variety" | "tradeDirection" | "token" | "selected"
>;

const Tab: React.FC<TabProps> = ({
  timeframe,
  isSelected,
  isDisabled,
  onClick,
  exchange,
  ltp,
  variety,
  tradeDirection,
  token,
  selected,
  quantity,
  segMentId,
  price,
}) => {
  const isMcx = segMentId === 5;
  const finalQuantity = isMcx ? 1 : quantity;

  const dispatch = useDispatch();

  const finalPrice = price === -1 ? ltp : price;
  
  // Handle edge case: ensure ltp is a valid number
  const validLtp = (!ltp || isNaN(ltp) || ltp <= 0) ? 1 : ltp;
  
  // Memoize payload object to prevent unnecessary re-renders
  const payload: OrderPayload = useMemo(
    () => ({
      token: +token,
      exchange,
      transaction_type: tradeDirection === "Buy" ? "BUY" : "SELL",
      product: getOrderProduct(selected) ?? "BTST",
      variety: variety,
      quantity: finalQuantity,
      price: finalPrice > 0 ? finalPrice : validLtp, // Ensure price is valid
      mode: "NEW",
      old_quantity: 0,
      old_price: 0.0,
    }),
    [token, exchange, tradeDirection, selected, variety, finalQuantity, finalPrice, validLtp]
  );

  const { data, loading, error } = useOrderRequest(payload);

  const { maxQuantity, multiplier, isMargin } = useMemo(() => {
    let maxQuantity: number | undefined;
    let multiplier: string | undefined;
    let isMargin: number | undefined;

    if (data) {
      if (isSuccessResponse(data)) {
        // Handle edge case: prevent division by zero
        const requiredMargin = data.required_margin > 0 ? data.required_margin : 1;
        
        // Handle edge case: ensure we don't get infinity
        maxQuantity = data.required_margin <= 0 
          ? 0 
          : Math.abs(Math.floor(data.available_margin / requiredMargin)) || 0;
          
        if (isSelected) {
          isMargin = data.available_margin;

          // Set data in store to reduce props drilling
          dispatch(setMargin({
            requiredMargin: requiredMargin,
            availableMargin: data.available_margin,
          }));
        }
        
        // Handle edge case: prevent infinity in multiplier calculation
        try {
          const calcMultiplier = orderMultiplier(
            tradeDirection,
            selected,
            requiredMargin,
            validLtp
          );
          
          // Handle edge cases with multiplier calculation
          multiplier = (!isFinite(calcMultiplier) || isNaN(calcMultiplier)) 
            ? "1.0" 
            : calcMultiplier.toFixed(1);
        } catch (err) {
          console.error("Multiplier calculation error:", err);
          multiplier = "1.0"; // Default fallback
        }
      } else if (isErrorResponse(data)) {
        console.error("Error Message:", data.message);
        // Set default values on error
        maxQuantity = 0;
        multiplier = "1.0";
      }
    } else if (error) {
      console.error("Error:", error);
      // Set default values on error
      maxQuantity = 0;
      multiplier = "1.0";
    }

    return { maxQuantity, multiplier, isMargin };
  }, [data, tradeDirection, isSelected, selected, error, validLtp]);

  // Calculate opacity based on selection state
  const opacityFade = !isSelected && isDisabled ? 0.7 : 1;
  const isFNO_OPT = segMentId !== 1 && segMentId !== 3;

  // Handle timeout for loading state
  const [loadingTimeout, setLoadingTimeout] = React.useState(false);
  
  React.useEffect(() => {
    // Set a timeout to show fallback values if loading takes too long
    if (loading) {
      const timer = setTimeout(() => {
        setLoadingTimeout(true);
      }, 5000); // 5 seconds timeout
      
      return () => clearTimeout(timer);
    } else {
      setLoadingTimeout(false);
    }
  }, [loading]);

  return (
    <div
      onClick={!isDisabled ? onClick : undefined}
      className={`py-2 px-4 gtt-gray-hover text-start ${
        isSelected
          ? "gtt-selected-border-bottom gtt-hover-gray font-700"
          : "font-400"
      } ${isDisabled ? "dull-opacity" : ""}`}
      style={{
        cursor: isDisabled ? "not-allowed" : "pointer",
        opacity: opacityFade,
        fontSize: 16,
        lineHeight: "1.2",
        height: 67,
      }}
    >
      {timeframe === 'T+5' ? 'T+5' : timeframe}
      <div
        style={{ fontWeight: 400, opacity: "0.7" }}
        className="font-10 text-nowrap"
      >
        {(maxQuantity !== undefined || loadingTimeout) ? (
          `Max Quantity ${maxQuantity || 0}`
        ) : (
          <RectangleSkelton style={{ width: "100%", height: 12 }} />
        )}
      </div>
      {(multiplier || loadingTimeout) ? (
        <ExchangePill text={`${isFNO_OPT ? "1.0" : (multiplier || "1.0")}x`} />
      ) : (
        <RectangleSkelton
          className="mt-1"
          style={{ width: 30, height: 15.1, borderRadius: 6 }}
        />
      )}
    </div>
  );
};

export default Tab;